import { CustomTabs, PageHeader, ErrorBoundary } from "@circuitry-ai/doc-ui";
import { useState, useEffect, Key } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityResourcePermissions } from "@circuitry-ai/doc-data";
import UXGenieConfiguration from "./UXGenieConfiguration";
import UXGenie from "./UXGenie";
import UXGenieTemplateHistory from "./UXGenieTemplateHistory";
import { Icon } from "@iconify/react";

export interface UXGenieViewProps {
    /** When inside Settings, use this path for back and tab nav (e.g. /settings/uxgenie) */
    basePath?: string;
}

export enum UXGenieViewTabs {
    CONFIGURATION = 'configuration',
    TEMPLATES = 'templates',
}

// Hoist ErrorBoundary-wrapped components to module scope
const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);
const CustomTabsWithErrorBoundary = ErrorBoundary(CustomTabs);
const UXGenieConfigurationWithErrorBoundary = ErrorBoundary(UXGenieConfiguration as unknown as React.ComponentType<Record<string, unknown>>);
const UXGenieWithErrorBoundary = ErrorBoundary(UXGenie as unknown as React.ComponentType<Record<string, unknown>>);
const UXGenieTemplateHistoryWithErrorBoundary = ErrorBoundary(UXGenieTemplateHistory as unknown as React.ComponentType<Record<string, unknown>>);

export function UXGenieView(props: UXGenieViewProps) {

    const { basePath = '/uxgenie' } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const { mode } = useParams<{ mode: string; }>();
    // Get aiworker_id from state or mode param (if it's not CREATE)
    const { uxgenieWorker } = location?.state ?? {};
    const aiworkerId = uxgenieWorker?.aiworker_id || (mode !== 'create' ? mode : undefined);
    const isCreateMode = mode === 'create';
    const { canUpdate } = useEntityResourcePermissions('uxgenie', isCreateMode ? undefined : aiworkerId);
    const disabledKeys = isCreateMode
        ? [UXGenieViewTabs.TEMPLATES]
        : !canUpdate
            ? [UXGenieViewTabs.CONFIGURATION]
            : [];

    const tabItems = [
        { key: UXGenieViewTabs.CONFIGURATION, title: 'Configuration', id: 'configuration-tab' },
        { key: UXGenieViewTabs.TEMPLATES, title: 'Templates', id: 'templates-tab' }
    ];

    const [currentTab, setCurrentTab] = useState<string>(tab ?? UXGenieViewTabs.CONFIGURATION);

    useEffect(() => {
        if (tab) {
            setCurrentTab(tab);
        } else {
            setCurrentTab(UXGenieViewTabs.CONFIGURATION);
        }
    }, [tab]);

    useEffect(() => {
        if (disabledKeys.includes(currentTab as UXGenieViewTabs)) {
            setCurrentTab(isCreateMode ? UXGenieViewTabs.CONFIGURATION : UXGenieViewTabs.TEMPLATES);
        }
    }, [disabledKeys, currentTab, isCreateMode]);

    const onTabChange = (tab: Key) => {
        setCurrentTab(tab as string);
        const { uxgenieWorker } = location?.state ?? {};
        navigate(`${basePath}/${mode}?tab=${tab}`, { state: { uxgenieWorker } });
    };

    const renderSelectedTab = () => {
        const { uxgenieWorker } = location?.state ?? {};
        const workerId = aiworkerId;

        switch (currentTab) {
            case UXGenieViewTabs.CONFIGURATION:
                return <UXGenieConfigurationWithErrorBoundary basePath={basePath} />;
            case UXGenieViewTabs.TEMPLATES:
                return <UXGenieTemplateHistoryWithErrorBoundary aiworkerId={workerId} basePath={basePath} />;
            default:
                return <UXGenieConfigurationWithErrorBoundary basePath={basePath} />;
        }
    };

    return (
        <div className="flex flex-col h-full gap-2">
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center gap-1.5 pt-4 pb-2 text-sm">
                <button
                    onClick={() => navigate(basePath)}
                    className="text-default-500 hover:text-blue-600 transition-colors"
                >
                    UXGenie Templates
                </button>
                <Icon icon="mdi:chevron-right" width={16} className="text-default-400 flex-shrink-0" />
                <span className="text-foreground font-medium">
                    {uxgenieWorker?.name || 'Worker Configuration'}
                </span>
            </nav>

            <div className="advisor-tab-list">
                <CustomTabsWithErrorBoundary
                    tabs={tabItems}
                    selectedKey={currentTab}
                    onTabChange={onTabChange}
                    disabledKeys={disabledKeys}
                />
            </div>
            <div className="h-[78vh] overflow-y-hidden">{renderSelectedTab()}</div>
        </div>
    );

}

export default UXGenieView;

